import { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useClinicians } from '@features/organizations';
import { useOrganizationId } from '@features/user/store/hooks';
import { Alert, Box } from '@mui/material';
import { SentryErrorBoundary } from '@services/SentryService';
import { useEventTracking } from '@services/statsig';

import { type RevenueFilters, useArAgingQuery, useCleanClaimsQuery } from './api';
import { readRevenueFilters, updateRevenueFilters } from './navigation';
import { RevenueDetail } from './RevenueDetail';
import { useRevenueAccess } from './useRevenueAccess';

export function RevenueAnalyticsPage({ embedded = false }: { embedded?: boolean }) {
  const enabled = useRevenueAccess();
  const { clinicians, isLoading: cliniciansLoading } = useClinicians({ skip: !enabled });
  const organizationId = useOrganizationId() ?? undefined;
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'ar' ? 'ar' : 'clean';
  const patientView = params.get('party') === 'patient';
  const filters = { ...readRevenueFilters(params), entityId: organizationId };
  const clean = useCleanClaimsQuery(filters, {
    skip: !enabled || view !== 'clean',
    refetchOnMountOrArgChange: true,
  });
  const ar = useArAgingQuery(
    { entityId: organizationId },
    {
      skip: !enabled || view !== 'ar',
      refetchOnMountOrArgChange: true,
    },
  );
  const query = view === 'clean' ? clean : ar;
  const { jotbillAnalytics } = useEventTracking();
  const opened = useRef(false);
  useEffect(() => {
    if (enabled && !opened.current) {
      opened.current = true;
      jotbillAnalytics.revenueOpened({ view });
    }
  }, [enabled, jotbillAnalytics, view]);
  const onFilters = (patch: Partial<RevenueFilters>) => {
    setParams(updateRevenueFilters(params, patch));
    // Filter field names only. Never log search text, payer names, or records.
    jotbillAnalytics.filterChanged({ filter_type: Object.keys(patch).join(','), view });
  };
  if (!enabled)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Revenue analytics is not available for this account.</Alert>
        <Link to="/jotbill">Back to JotBill</Link>
      </Box>
    );
  return (
    <SentryErrorBoundary>
      <RevenueDetail
        embedded={embedded}
        key={`${organizationId}:${view}`}
        view={view}
        patientView={patientView}
        responsibility={
          patientView
            ? 'patient'
            : ['insurance', 'claims'].includes(params.get('party') ?? '')
              ? 'insurance'
              : 'all'
        }
        onResponsibility={party => {
          setParams({
            ...(params.get('tab') === 'analytics' ? { tab: 'analytics' } : {}),
            view: 'ar',
            party,
          });
          jotbillAnalytics.revenueClicked({ view, action: `responsibility_${party}` });
        }}
        filters={filters}
        clinicians={clinicians.map(c => ({ value: c.userId, label: c.name }))}
        cliniciansLoading={cliniciansLoading}
        clean={clean.currentData?.data}
        ar={ar.currentData?.data}
        loading={query.isFetching}
        error={query.isError}
        onRetry={() => {
          query.refetch();
          jotbillAnalytics.revenueClicked({ view, action: 'retry' });
        }}
        onView={next => {
          setParams({
            ...(params.get('tab') === 'analytics' ? { tab: 'analytics' } : {}),
            view: next,
          });
          jotbillAnalytics.revenueClicked({ view: next, action: 'change_view' });
        }}
        onPatientView={patient => {
          const next = new URLSearchParams(params);
          next.set('party', patient ? 'patient' : 'claims');
          next.delete('patientOffset');
          next.delete('offset');
          setParams(next);
          jotbillAnalytics.revenueClicked({
            view,
            action: patient ? 'patient_balances' : 'claim_balances',
          });
        }}
        onFilters={onFilters}
        onAction={action => jotbillAnalytics.revenueClicked({ view, action })}
      />
    </SentryErrorBoundary>
  );
}
